<template>
  <div class="nx-signing-modal-method-p12">
    <div v-if="loginState !== 'success'">
      <div v-if="loginState === 'checking'">
        <h4>{{ $t('CheckIAMSmart') }}</h4>

        <p class="mt-4">
          <b-spinner class="m-auto" />
        </p>
      </div>
      <div v-else-if="loginState === 'fail'">
        <h4>{{ $t('FailGetIAMSmart') }}</h4>

        <p class="mt-4">
          <IAMSmartLoginButton
            class="mx-auto mb-2"
            @click="loginState = 'retry'"
          />
          <IAMSmartLink />
        </p>
      </div>
      <div v-else-if="loginState === 'retry'">
        <h4>{{$t('RefreshAfterIAMSmartLogin')}}</h4>

        <p class="mt-4">
          <b-button class="m-auto" @click="acquireAccessToken">{{$t('Retry')}}</b-button>
        </p>
      </div>
    </div>

    <div v-else>
      <h4>
        {{ $t('iamSmart.signingDesktop.info', { docType: $t('docType') }) }}
      </h4>

      <p>
        <span class="instruct-label" v-text="$t('serviceNameLabel')" />
        {{ $t('serviceName') }}
      </p>

      <p>
        <span class="instruct-label" v-text="$t('docType')" />
        {{ file.name }}
      </p>

      <p>
        <span
          class="instruct-label"
          v-text="$t('iamSmart.signingDesktop.idCode')"
        />
        <span class="signCode">{{ file.signCode }}</span>
      </p>

      <b-button class="my-4" @click="sign(file)">{{$t('StartSigning')}}</b-button>

      <b-container class="text-left">
        <p
          class="instruct-label"
          v-text="$t('iamSmart.signingDesktop.instructTitle')"
        />
        <ol class="instruct-list">
          <li>
            {{ $t('iamSmart.signingDesktop.instructStep1') }}
            <b-img
              style="height: 35px"
              :src="
                require('@nextore/iam-smart/dist/assets/iamsmart/tapToOpen/tap.png')
              "
            />
          </li>
          <li>{{ $t('iamSmart.signingDesktop.instructStep2') }}</li>
          <li>{{ $t('iamSmart.signingDesktop.instructStep3') }}</li>
        </ol>
      </b-container>
    </div>
  </div>
</template>

<script>
import {
  IAMSmartAuthApi,
  IAMSmartLoginButton,
  IAMSmartLink
} from '@nextore/iam-smart';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { EssSignApi } from '@/api';
import { FileApi } from 'nx-libs';

const callbackKeyMixin = {
  created() {
    this._callbackKeys = {};
  },
  methods: {
    addCallbackKey(key) {
      this._callbackKeys[key] = true;
    },
    checkAndRemoveCallbackKey(key) {
      if (this._callbackKeys[key]) {
        delete this._callbackKeys[key];

        return true;
      }

      return false;
    }
  }
};

const signResultMixin = {
  created() {
    const connection = new HubConnectionBuilder()
      .withUrl('/IAmSmart/SignResult')
      .build();

    connection.on('OnSignResult', (businessID, success) => {
      if (!this.checkAndRemoveCallbackKey(businessID)) {
        return;
      }

      const file = this.businessIdMap[businessID];

      if (success) {
        file.resolve();
      } else {
        file.reject(
          `Fail to sign document with business id ${file.businessId}`
        );
      }
    });

    connection
      .start()
      .then(() => {
        this._connection = connection;
      })
      .catch(function (err) {
        this.$dangerToast(
          'Setup iamSmart websocket',
          'Fail to connect iamSmart websocket'
        );

        throw err;
      });
  }
};

export default {
  mixins: [signResultMixin, callbackKeyMixin],
  components: { IAMSmartLoginButton, IAMSmartLink },
  props: {
    value: {},
    isNonPdf: {
      type: Boolean,
      default: false
    },
    isDrawing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loginState: null,
      file: null,
      businessIdMap: {}
    };
  },
  watch: {
    'value.fileId': {
      immediate: true,
      handler(fileId) {
        this.file = {
          id: fileId,
          signCode: null,
          status: 'pending',
          businessId: null,
          resolve: null,
          reject: null
        };
      }
    },
    file: {
      deep: true,
      handler(file) {
        if (file.status === 'Signed') {
          this.$emit('next');
        }
      }
    }
  },
  async created() {
    const { data: fileInfo } = await FileApi.getFileInfo(this.file.id);
    this.file.name = fileInfo.name;
    await this.acquireAccessToken();
  },
  methods: {
    async acquireAccessToken() {
      this.loginState = 'checking';

      const { data: hasToken } = await IAMSmartAuthApi.hasAccessToken();

      this.loginState = hasToken ? 'success' : 'fail';
    },
    async sign(file) {
      var dataFinal = {};
      if (this.isDrawing) {
        const { data } = await EssSignApi.digestPdfIAMSmart(file.id);
        dataFinal = data;
      } else if (this.isNonPdf) {
        const { data } = await EssSignApi.digestNonPdfIAMSmart(file.id);
        dataFinal = data;
      } else {
        const { data } = await EssSignApi.digestNormalPdfIAMSmart(file.id);
        dataFinal = data;
      }

      file.signCode = dataFinal.signCode;
      file.businessId = dataFinal.businessID;

      this.businessIdMap[file.businessId] = file;

      const prom = new Promise((res, rej) => {
        file.resolve = res;
        file.reject = rej;
      });

      this.addCallbackKey(dataFinal.businessID);
      this._connection.invoke('Listen', dataFinal.businessID);

      await prom;

      file.status = 'Signed';
    }
  },
  i18n: {
    messages: {
      en: {
        docType: 'Document',
        serviceNameLabel: 'Service name',
        serviceName: 'Electronic Submission Hub (ESH)',
        FailGetIAMSmart: 'Fail to get \"iAM Smart\" session',
        CheckIAMSmart: 'Checking for \"iAM Smart\" session',
        RefreshAfterIAMSmartLogin: 'Please refresh after IAMSmart login',
        Retry: 'Retry', 
        StartSigning: 'Start signing',
        IamSmartReminder : 'Your "iAM Smart" account does not provide digital signing function. Please upgrade the account to get digital signing function. For details about upgrade of "iAM Smart" account, please refer to the iAM Smart website.'

      },
      zh_hk: {
        docType: '文件',
        serviceNameLabel: '服務名稱',
        serviceName: '電子資料呈交及處理系統',
        FailGetIAMSmart: '無法獲取 「智方便」 會議',
        CheckIAMSmart: '檢查 IAMSmart 會議',
        RefreshAfterIAMSmartLogin: '請在 「智方便」 登入後刷新',
        Retry: '重試',
        StartSigning: '開始簽署',
        IamSmartReminder : '你的「智方便」戶口未有提供數碼簽署功能 ，請將現在的「智方便」户口升級以獲得數碼簽署功能。有關於升級「智方便」戶口之詳情，請參閱「智方便」網站。'
      },
      zh_cn: {
        docType: '文件',
        serviceNameLabel: '服务名称',
        serviceName: '电子资料呈交及处理系统',
        FailGetIAMSmart: '无法获取「智方便」会议',
        CheckIAMSmart: '检查 IAMSmart 会议',
        RefreshAfterIAMSmartLogin: '请在 「智方便」 登入后刷新',
        Retry: '重试',
        StartSigning: '开始签署',
        IamSmartReminder : '你的「智方便」户口未有提供数码签署功能 ，请将现在的「智方便」户口升级以获得数码签署功能。有关于升级「智方便」户口之详情，请参阅「智方便」网站。'
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.instruct-label::after {
  content: ':';
}

.instruct-list li {
  line-height: 35px;

  &:first-child {
    color: #2b7366;
  }
}
.signCode {
  color: #2b7367;
}

.nx-signing-modal-method-p12 {
  text-align: center;
}
</style>
